import React, { createContext, useContext, useState, useEffect } from 'react';
import * as Toast from '@radix-ui/react-toast';
import { Box, Typography } from '@mui/material';
import { 
  CheckCircle, 
  Error as ErrorIcon, 
  Warning, 
  Info, 
  Close 
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { setGlobalToastInstance } from '../utils/toast';

// Types
export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
}

interface ToastContextType {
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
}

// Context
const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Provider component
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const theme = useTheme();

  const addToast = (type: ToastType, message: string) => {
    const id = Math.random().toString(36).substring(2);
    const newToast: ToastMessage = { id, type, message };
    setToasts(prev => [...prev, newToast]);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 5000);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const getIcon = (type: ToastType) => {
    switch (type) {
      case 'success':
        return <CheckCircle fontSize="small" />;
      case 'error':
        return <ErrorIcon fontSize="small" />;
      case 'warning':
        return <Warning fontSize="small" />;
      case 'info':
        return <Info fontSize="small" />;
      default:
        return <Info fontSize="small" />;
    }
  };

  const getTypeStyles = (type: ToastType) => {
    switch (type) {
      case 'success':
        return {
          backgroundColor: theme.palette.success.main,
          color: theme.palette.success.contrastText,
          borderColor: theme.palette.success.dark,
        };
      case 'error':
        return {
          backgroundColor: theme.palette.error.main,
          color: theme.palette.error.contrastText,
          borderColor: theme.palette.error.dark,
        };
      case 'warning':
        return {
          backgroundColor: theme.palette.warning.main,
          color: theme.palette.warning.contrastText,
          borderColor: theme.palette.warning.dark,
        };
      case 'info':
        return {
          backgroundColor: theme.palette.info.main,
          color: theme.palette.info.contrastText,
          borderColor: theme.palette.info.dark,
        };
      default:
        return {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderColor: theme.palette.primary.dark,
        };
    }
  };

  const contextValue: ToastContextType = {
    success: (message: string) => addToast('success', message),
    error: (message: string) => addToast('error', message),
    warning: (message: string) => addToast('warning', message),
    info: (message: string) => addToast('info', message),
  };

  // Set global toast instance for utility classes
  useEffect(() => {
    setGlobalToastInstance(contextValue);
  }, []);

  return (
    <ToastContext.Provider value={contextValue}>
      <Toast.Provider swipeDirection="right">
        {children}
        <Toast.Viewport
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            display: 'flex',
            flexDirection: 'column',
            padding: theme.spacing(3),
            gap: theme.spacing(1),
            width: '390px',
            maxWidth: '100vw',
            margin: 0,
            listStyle: 'none',
            zIndex: 2147483647,
            outline: 'none',
          }}
        >
          {toasts.map(toast => (
            <Toast.Root
              key={toast.id}
              duration={5000}
              onOpenChange={(open) => {
                if (!open) {
                  removeToast(toast.id);
                }
              }}
              style={{
                ...getTypeStyles(toast.type),
                borderRadius: theme.shape.borderRadius,
                border: `1px solid`,
                padding: theme.spacing(2),
                display: 'flex',
                alignItems: 'flex-start',
                gap: theme.spacing(1.5),
                boxShadow: theme.shadows[6],
                position: 'relative',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                {getIcon(toast.type)}
                <Toast.Description
                  style={{
                    margin: 0,
                    fontSize: '14px',
                    lineHeight: 1.4,
                    flex: 1,
                  }}
                >
                  {toast.message}
                </Toast.Description>
                <Toast.Action
                  altText="Close"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: theme.spacing(0.5),
                    borderRadius: theme.shape.borderRadius,
                    marginLeft: 'auto',
                    color: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Close fontSize="small" />
                </Toast.Action>
              </Box>
            </Toast.Root>
          ))}
        </Toast.Viewport>
      </Toast.Provider>
    </ToastContext.Provider>
  );
};

// Hook to use toast
export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Export individual toast functions for backward compatibility
export const toast = {
  success: (message: string) => {
    console.warn('Direct toast usage detected. Please use useToast hook instead.');
  },
  error: (message: string) => {
    console.warn('Direct toast usage detected. Please use useToast hook instead.');
  },
  warning: (message: string) => {
    console.warn('Direct toast usage detected. Please use useToast hook instead.');
  },
  info: (message: string) => {
    console.warn('Direct toast usage detected. Please use useToast hook instead.');
  },
};